import { CollisionEvent } from "./CollisionEvent";
import { Entity } from "./Entity";
import { BOUNCE, TERMINAL_VELOCITY, TICKS, UNIT } from "./config";

export class EntityRunner extends AbortController {
  interval: number;

  constructor(readonly entities: Set<Entity>) {
    super();
    this.interval = setInterval(this.step.bind(this), 1000 / TICKS);
  }

  abort() {
    super.abort();
    clearInterval(this.interval);
  }

  step() {
    for (const entity of this.entities) this.updateEntity(entity);
    for (const entity of this.entities) this.checkCollisions(entity);
  }

  updateEntity(entity: Entity) {
    this.applyForces(entity);
    this.clipVelocity(entity);
    this.applyVelocity(entity);
    this.wrapPosition(entity);
  }

  applyForces(entity: Entity) {
    for (const force of Object.values(entity.forces)) {
      entity.velocity.x += force.x / TICKS;
      entity.velocity.y += force.y / TICKS;
    }
  }

  clipVelocity(entity: Entity) {
    const { x, y } = entity.velocity;
    const termvel = TERMINAL_VELOCITY / TICKS;
    entity.velocity.x = Math.max(-termvel, Math.min(x, termvel));
    entity.velocity.y = Math.max(-termvel, Math.min(y, termvel));
  }

  applyVelocity(entity: Entity) {
    entity.position.x += entity.velocity.x;
    entity.position.y += entity.velocity.y;
    entity.angle += entity.angle_velocity;
  }

  wrapPosition(entity: Entity) {
    const { position } = entity;
    const width = innerWidth / UNIT;
    if (position.x > width / 2) position.x -= width;
    else if (position.x < -width / 2) position.x += width;
    const height = innerHeight / UNIT;
    if (position.y > height / 2) position.y -= height;
    else if (position.y < -height / 2) position.y += height;
  }

  checkCollisions(entity: Entity) {
    for (const other of this.entities) {
      if (other === entity) continue;
      if (!this.collides(entity, other)) continue;
      entity.dispatchEvent(new CollisionEvent(entity, other));
      entity.velocity = entity.position.sub(other.position).unit().mul(BOUNCE);
    }
  }

  collides(a: Entity, b: Entity): boolean {
    const distance = a.position.distance(b.position);
    return distance < a.radius + b.radius;
  }
}
